import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, chatApi, chatFilesApi, supportApi, approvalsApi, getWebSocketUrl, formatApiErrorDetail } from '../../lib/api';
import { cn, formatDate } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  MessageCircle, Send, Loader2, User, Building2, FileText, ShieldCheck, AlertTriangle,
  Clock, CheckCircle, XCircle, Star, StickyNote, ChevronRight, Paperclip, Edit3,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const STATUS_COLORS = {
  OPEN: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  WAITING_USER: 'bg-orange-100 text-orange-700',
  CLOSED: 'bg-slate-100 text-slate-500',
};

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.value = 0.12;
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  } catch (e) { /* ignore */ }
}

export default function SupportDashboard() {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userContext, setUserContext] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('me');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typing, setTyping] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showModRequest, setShowModRequest] = useState(false);
  const [modForm, setModForm] = useState({ entity_type: 'sale', entity_id: '', action_type: 'UPDATE', description: '' });
  const [submittingMod, setSubmittingMod] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch chats
  const fetchChats = useCallback(async () => {
    try {
      const params = { limit: 100 };
      if (filter === 'me') params.assigned_to = 'me';
      else if (filter === 'unassigned') params.assigned_to = 'unassigned';
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await chatApi.list(params);
      setChats(data.items || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [filter, statusFilter]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await supportApi.getAnalytics();
      setAnalytics(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchChats(); fetchAnalytics(); }, [fetchChats, fetchAnalytics]);

  // Fetch messages
  const fetchMessages = async (chatId) => {
    try {
      const { data } = await chatApi.getMessages(chatId, { limit: 200 });
      setMessages(data.items || []);
      setTimeout(scrollToBottom, 100);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'mark_read', chat_id: chatId }));
      }
    } catch (e) { /* ignore */ }
  };

  // Fetch user context
  const fetchUserContext = async (chatId) => {
    try {
      const { data } = await chatApi.getUserContext(chatId);
      setUserContext(data);
    } catch (e) { setUserContext(null); }
  };

  // WebSocket
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const connect = () => {
      const ws = new WebSocket(getWebSocketUrl(token));
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          const msg = data.message;
          setMessages(prev => {
            if (prev.some(m => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          setTimeout(scrollToBottom, 100);
          if (msg.sender_id !== user.id && msg.message_type !== 'SYSTEM') {
            playNotificationSound();
          }
          fetchChats();
        } else if (data.type === 'new_chat' || data.type === 'chat_assigned') {
          fetchChats();
          playNotificationSound();
        } else if (data.type === 'typing') {
          setTyping(data.user_name);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTyping(null), 2000);
        } else if (data.type === 'messages_read') {
          setMessages(prev => prev.map(m => m.sender_id === user.id ? { ...m, status: 'READ' } : m));
        } else if (data.type === 'chat_status_changed') {
          fetchChats();
          if (data.message) {
            setMessages(prev => {
              if (prev.some(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          }
        }
      };

      ws.onclose = () => { setTimeout(connect, 3000); };
    };

    connect();
    return () => { if (wsRef.current) wsRef.current.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Open chat
  const openChat = (chat) => {
    setActiveChat(chat);
    setShowContext(false);
    fetchMessages(chat.id);
    fetchUserContext(chat.id);
  };

  // Send message
  const handleSend = () => {
    if (!newMessage.trim() || !activeChat || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({
      type: 'send_message',
      chat_id: activeChat.id,
      message: newMessage.trim(),
      message_type: isInternalNote ? 'INTERNAL_NOTE' : 'TEXT',
    }));
    setNewMessage('');
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChat || !wsRef.current) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10 MB)'); return; }
    setUploading(true);
    try {
      const { data } = await chatFilesApi.upload(file);
      wsRef.current.send(JSON.stringify({
        type: 'send_message',
        chat_id: activeChat.id,
        message: file.name,
        message_type: 'FILE',
        file_url: data.id,
        file_name: data.original_filename,
      }));
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Submit modification request
  const handleSubmitModRequest = async () => {
    if (!modForm.description.trim()) { toast.error('Description required'); return; }
    setSubmittingMod(true);
    try {
      await approvalsApi.create({ ...modForm, chat_id: activeChat?.id });
      toast.success('Modification request submitted for admin approval');
      setShowModRequest(false);
      setModForm({ entity_type: 'sale', entity_id: '', action_type: 'UPDATE', description: '' });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmittingMod(false);
    }
  };

  // Send typing
  const handleTyping = () => {
    if (activeChat && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', chat_id: activeChat.id }));
    }
  };

  // Update chat status
  const handleStatusChange = async (chatId, status) => {
    try {
      await chatApi.updateStatus(chatId, { status });
      toast.success(`Chat ${status.toLowerCase().replace('_', ' ')}`);
      fetchChats();
      if (activeChat?.id === chatId) {
        setActiveChat(prev => ({ ...prev, status }));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Pick up unassigned chat
  const handlePickup = async (chatId) => {
    try {
      await chatApi.assign(chatId, {});
      toast.success('Chat assigned to you');
      fetchChats();
      const { data } = await chatApi.get(chatId);
      setActiveChat(data);
      fetchMessages(chatId);
      fetchUserContext(chatId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="support-dashboard">
        {/* Stats Bar */}
        {analytics && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
            {[
              { label: 'Open', value: analytics.open, color: 'text-blue-600' },
              { label: 'In Progress', value: analytics.in_progress, color: 'text-yellow-600' },
              { label: 'Waiting', value: analytics.waiting_user, color: 'text-orange-600' },
              { label: 'Unassigned', value: analytics.unassigned, color: 'text-red-600' },
              { label: 'Closed', value: analytics.closed, color: 'text-slate-500' },
              { label: 'Avg Rating', value: analytics.avg_rating || '-', color: 'text-emerald-600' },
            ].map(s => (
              <Card key={s.label} className="border-slate-200">
                <CardContent className="p-3 text-center">
                  <p className={cn('text-2xl font-bold', s.color)}>{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Main Chat Area */}
        <div className="flex flex-1 gap-4 min-h-0">
          {/* Chat List Sidebar */}
          <div className="w-80 shrink-0 flex flex-col border rounded-xl bg-white overflow-hidden">
            <div className="p-3 border-b space-y-2">
              <h2 className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>Chats</h2>
              <div className="flex gap-2">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">My Chats</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="OPEN">Open</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="WAITING_USER">Waiting</SelectItem>
                    <SelectItem value="CLOSED">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-[#005DE8]" /></div>
              ) : chats.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-8">No chats found</p>
              ) : (
                chats.map(chat => (
                  <button
                    key={chat.id}
                    onClick={() => openChat(chat)}
                    data-testid={`agent-chat-${chat.id}`}
                    className={cn(
                      'w-full text-left p-3 border-b border-slate-50 hover:bg-slate-50 transition',
                      activeChat?.id === chat.id && 'bg-blue-50'
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-slate-900 truncate">{chat.user_name}</span>
                      {chat.unread_agent > 0 && (
                        <span className="w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center">{chat.unread_agent}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{chat.subject}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-slate-400 truncate">{chat.last_message || 'No messages'}</span>
                      <Badge className={cn('text-[9px] ml-1 shrink-0', STATUS_COLORS[chat.status])}>{chat.status?.replace('_', ' ')}</Badge>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Window */}
          <div className="flex-1 flex flex-col border rounded-xl bg-white overflow-hidden">
            {!activeChat ? (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Select a chat to start responding</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="p-3 border-b flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm truncate">{activeChat.user_name}</h3>
                      <Badge className="text-[10px]">{activeChat.user_role}</Badge>
                      <Badge className={cn('text-[10px]', STATUS_COLORS[activeChat.status])}>{activeChat.status?.replace('_', ' ')}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{activeChat.subject}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setShowContext(!showContext)}
                      className="text-xs"
                      data-testid="toggle-user-context"
                    >
                      <User className="h-4 w-4 mr-1" /> Context
                    </Button>
                    {!activeChat.assigned_agent_id && (
                      <Button size="sm" onClick={() => handlePickup(activeChat.id)} className="bg-[#005DE8] text-xs h-7" data-testid="pickup-chat-button">
                        Pick Up
                      </Button>
                    )}
                    {activeChat.status !== 'CLOSED' && (
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(activeChat.id, 'CLOSED')} className="text-xs h-7 text-red-600 border-red-200" data-testid="close-chat-button">
                        <XCircle className="h-3 w-3 mr-1" /> Close
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-1 min-h-0">
                  {/* Messages */}
                  <div className="flex-1 flex flex-col">
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {messages.map(msg => (
                        <div key={msg.id} className={cn(
                          'flex',
                          msg.message_type === 'SYSTEM' ? 'justify-center' :
                          msg.message_type === 'INTERNAL_NOTE' ? 'justify-end' :
                          msg.sender_id === user.id ? 'justify-end' : 'justify-start'
                        )}>
                          {msg.message_type === 'SYSTEM' ? (
                            <span className="text-[11px] text-slate-400 bg-slate-50 px-2 py-1 rounded-full">{msg.message}</span>
                          ) : msg.message_type === 'INTERNAL_NOTE' ? (
                            <div className="max-w-[80%] px-3 py-2 rounded-2xl text-sm bg-amber-50 border border-amber-200 rounded-br-md">
                              <p className="text-[10px] font-medium text-amber-600 mb-0.5 flex items-center gap-1"><StickyNote className="h-3 w-3" /> Internal Note</p>
                              <p className="text-slate-700 whitespace-pre-wrap break-words">{msg.message}</p>
                              <span className="text-[9px] text-amber-400 block text-right mt-0.5">
                                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          ) : msg.message_type === 'FILE' ? (
                            <div className={cn(
                              'max-w-[80%] px-3 py-2 rounded-2xl text-sm',
                              msg.sender_id === user.id ? 'bg-[#005DE8] text-white rounded-br-md' : 'bg-slate-100 text-slate-900 rounded-bl-md'
                            )}>
                              {msg.sender_id !== user.id && <p className="text-[10px] font-medium mb-0.5 opacity-70">{msg.sender_name}</p>}
                              {msg.file_name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img src={chatFilesApi.getUrl(msg.file_url)} alt={msg.file_name} className="max-w-[200px] rounded-lg mb-1 cursor-pointer" onClick={() => window.open(chatFilesApi.getUrl(msg.file_url), '_blank')} />
                              ) : (
                                <a href={chatFilesApi.getUrl(msg.file_url)} target="_blank" rel="noopener noreferrer" className={cn('flex items-center gap-2 px-2 py-1.5 rounded-lg', msg.sender_id === user.id ? 'bg-white/20 hover:bg-white/30' : 'bg-white hover:bg-slate-50 border')}>
                                  <FileText className="h-4 w-4 shrink-0" />
                                  <span className="text-xs truncate">{msg.file_name || 'File'}</span>
                                </a>
                              )}
                              <div className={cn('flex items-center justify-end gap-1 mt-0.5', msg.sender_id === user.id ? 'text-blue-200' : 'text-slate-400')}>
                                <span className="text-[9px]">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                          ) : (
                            <div className={cn(
                              'max-w-[80%] px-3 py-2 rounded-2xl text-sm',
                              msg.sender_id === user.id ? 'bg-[#005DE8] text-white rounded-br-md' : 'bg-slate-100 text-slate-900 rounded-bl-md'
                            )}>
                              {msg.sender_id !== user.id && (
                                <p className="text-[10px] font-medium mb-0.5 opacity-70">{msg.sender_name}</p>
                              )}
                              <p className="whitespace-pre-wrap break-words">{msg.message}</p>
                              <div className={cn('flex items-center justify-end gap-1 mt-0.5', msg.sender_id === user.id ? 'text-blue-200' : 'text-slate-400')}>
                                <span className="text-[9px]">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                {msg.sender_id === user.id && <span className="text-[9px]">{msg.status === 'READ' ? '✓✓' : '✓'}</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      {typing && <div className="text-xs text-slate-400 italic">{typing} is typing...</div>}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    {activeChat.status !== 'CLOSED' && activeChat.assigned_agent_id === user.id && (
                      <div className="p-2 border-t">
                        <div className="flex items-center gap-1 mb-1">
                          <button
                            onClick={() => setIsInternalNote(!isInternalNote)}
                            className={cn('text-[10px] px-2 py-0.5 rounded-full border transition', isInternalNote ? 'bg-amber-50 border-amber-300 text-amber-700' : 'border-slate-200 text-slate-400')}
                            data-testid="toggle-internal-note"
                          >
                            <StickyNote className="h-3 w-3 inline mr-1" />{isInternalNote ? 'Internal Note' : 'Reply'}
                          </button>
                          <button
                            onClick={() => setShowModRequest(true)}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-400 hover:border-purple-300 hover:text-purple-600 transition"
                            data-testid="request-modification-button"
                          >
                            <Edit3 className="h-3 w-3 inline mr-1" />Request Change
                          </button>
                        </div>
                        <div className="flex gap-1.5">
                          <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx" />
                          <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="h-9 w-9 p-0 shrink-0" data-testid="agent-attach-button">
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                          </Button>
                          <Input
                            placeholder={isInternalNote ? "Add internal note..." : "Type a reply..."}
                            value={newMessage}
                            onChange={e => { setNewMessage(e.target.value); handleTyping(); }}
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
                            className={cn('flex-1 text-sm h-9', isInternalNote && 'border-amber-300 bg-amber-50')}
                            data-testid="agent-message-input"
                          />
                          <Button size="sm" onClick={handleSend} disabled={!newMessage.trim()} className="bg-[#005DE8] h-9 w-9 p-0" data-testid="agent-send-button">
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* User Context Panel */}
                  {showContext && userContext && (
                    <div className="w-64 border-l overflow-y-auto p-3 space-y-3 bg-slate-50">
                      <h4 className="font-semibold text-xs text-slate-500 uppercase tracking-wider">User Context</h4>
                      <div className="space-y-2">
                        <div className="bg-white rounded-lg p-2 border">
                          <p className="text-xs text-slate-400">User</p>
                          <p className="font-medium text-sm">{userContext.name}</p>
                          <p className="text-xs text-slate-500">{userContext.email}</p>
                          <Badge className="text-[10px] mt-1">{userContext.role}</Badge>
                        </div>

                        {userContext.tenant && (
                          <div className="bg-white rounded-lg p-2 border">
                            <p className="text-xs text-slate-400 flex items-center gap-1"><Building2 className="h-3 w-3" /> Tenant</p>
                            <p className="font-medium text-sm">{userContext.tenant.company_name}</p>
                            <p className="text-xs text-slate-500">{userContext.tenant.contract_type} · {userContext.tenant.status}</p>
                          </div>
                        )}

                        {userContext.branch && (
                          <div className="bg-white rounded-lg p-2 border">
                            <p className="text-xs text-slate-400">Branch</p>
                            <p className="font-medium text-sm">{userContext.branch.branch_name}</p>
                            <p className="text-xs text-slate-500">{userContext.branch.location}</p>
                          </div>
                        )}

                        {userContext.recent_sales?.length > 0 && (
                          <div className="bg-white rounded-lg p-2 border">
                            <p className="text-xs text-slate-400 flex items-center gap-1"><FileText className="h-3 w-3" /> Recent Sales</p>
                            {userContext.recent_sales.map((s, i) => (
                              <div key={i} className="text-xs py-1 border-b last:border-0 border-slate-50">
                                <span className="font-medium">{s.device_brand} {s.device_model}</span>
                                <span className="text-slate-400 ml-1">{s.sale_date}</span>
                                {s.warranty_attached && <ShieldCheck className="h-3 w-3 text-emerald-500 inline ml-1" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {userContext.recent_invoices?.length > 0 && (
                          <div className="bg-white rounded-lg p-2 border">
                            <p className="text-xs text-slate-400">Recent Invoices</p>
                            {userContext.recent_invoices.map((inv, i) => (
                              <div key={i} className="text-xs py-1 border-b last:border-0 border-slate-50">
                                <span className="font-medium">{inv.invoice_number}</span>
                                <Badge className={cn('text-[9px] ml-1', inv.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700')}>{inv.status}</Badge>
                              </div>
                            ))}
                          </div>
                        )}

                        {userContext.open_claims?.length > 0 && (
                          <div className="bg-white rounded-lg p-2 border">
                            <p className="text-xs text-slate-400 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Open Claims</p>
                            {userContext.open_claims.map((c, i) => (
                              <div key={i} className="text-xs py-1 border-b last:border-0 border-slate-50">
                                <span className="font-medium">{c.customer_name}</span>
                                <span className="text-slate-400 ml-1">{c.issue_type}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modification Request Dialog */}
      <Dialog open={showModRequest} onOpenChange={setShowModRequest}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Request Data Modification</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Entity Type</Label>
                <Select value={modForm.entity_type} onValueChange={v => setModForm({ ...modForm, entity_type: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="mod-entity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['sale', 'invoice', 'tenant', 'branch', 'warranty', 'claim', 'user'].map(t => (
                      <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Action</Label>
                <Select value={modForm.action_type} onValueChange={v => setModForm({ ...modForm, action_type: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid="mod-action-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['UPDATE', 'DELETE', 'REFUND', 'STATUS_CHANGE', 'OTHER'].map(t => (
                      <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Entity ID (optional)</Label>
              <Input placeholder="e.g., invoice number, sale ID..." value={modForm.entity_id} onChange={e => setModForm({ ...modForm, entity_id: e.target.value })} className="h-8 text-xs" data-testid="mod-entity-id" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Description *</Label>
              <Textarea placeholder="Describe the change needed and why..." value={modForm.description} onChange={e => setModForm({ ...modForm, description: e.target.value })} rows={3} className="text-xs" data-testid="mod-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowModRequest(false)}>Cancel</Button>
            <Button size="sm" className="bg-[#005DE8]" onClick={handleSubmitModRequest} disabled={submittingMod || !modForm.description.trim()} data-testid="mod-submit">
              {submittingMod ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
